/// Low-precision Moon position — GHA, Dec, and the date-dependent
/// numbers a sextant Moon sight actually needs (Horizontal Parallax
/// and Semi-Diameter).
///
/// We use Meeus's truncated series with the top ~12 periodic terms,
/// giving position accuracy around 10–20 arc-minutes. That's about an
/// order of magnitude better than the Sun's parallax error on a Moon
/// sight (the Moon's parallax can shift Ho by nearly a degree), so the
/// truncation cost is well below the dominant correction.
///
/// For real-precision Moon sights students should reach for the
/// current Nautical Almanac; this module's job is to keep the
/// teaching loop self-contained and pen-and-paper-checkable.

import { DEG, RAD, daysSinceJ2000, normalize360 } from "./time";
import { sunPosition } from "./sun";

export interface MoonPosition {
  /// Declination of the Moon, degrees (N positive, S negative).
  decDeg: number;
  /// Greenwich Hour Angle of the Moon, degrees [0, 360).
  ghaDeg: number;
  /// Right ascension of the Moon, decimal hours [0, 24).
  raHours: number;
  /// Earth–Moon distance, kilometres.
  distanceKm: number;
  /// Horizontal parallax, arc-minutes. Drives the parallax-in-altitude
  /// correction (≈ 54′ at apogee, ≈ 61′ at perigee).
  hpArcmin: number;
  /// Apparent semi-diameter, arc-minutes (= 0.2725 · HP).
  sdArcmin: number;
}

const EARTH_RADIUS_KM = 6378.14;
const MOON_RADIUS_KM = 1737.4;

export function moonPosition(date: Date): MoonPosition {
  const n = daysSinceJ2000(date);
  const T = n / 36525; // Julian centuries from J2000

  // Mean elements, degrees.
  const Lp = normalize360(218.3164477 + 481267.88123421 * T);
  const M = normalize360(357.5291092 + 35999.0502909 * T);
  const Mp = normalize360(134.9633964 + 477198.8675055 * T);
  const D = normalize360(297.8501921 + 445267.1114034 * T);
  const F = normalize360(93.272095 + 483202.0175233 * T);

  // Helpers — degrees → radians.
  const dr = DEG;

  // Ecliptic longitude correction (Meeus top terms, arc-degrees).
  const lambdaDeg =
    Lp +
    6.289 * Math.sin(Mp * dr) +
    1.274 * Math.sin((2 * D - Mp) * dr) +
    0.658 * Math.sin((2 * D) * dr) -
    0.186 * Math.sin(M * dr) -
    0.059 * Math.sin((2 * Mp - 2 * D) * dr) -
    0.057 * Math.sin((Mp - 2 * D + M) * dr) +
    0.053 * Math.sin((Mp + 2 * D) * dr) +
    0.046 * Math.sin((2 * D - M) * dr) +
    0.041 * Math.sin((Mp - M) * dr) -
    0.035 * Math.sin(D * dr) -
    0.031 * Math.sin((Mp + M) * dr);

  // Ecliptic latitude (top terms).
  const betaDeg =
    5.128 * Math.sin(F * dr) +
    0.281 * Math.sin((Mp + F) * dr) -
    0.278 * Math.sin((F - Mp) * dr) -
    0.173 * Math.sin((F - 2 * D) * dr);

  // Earth–Moon distance, km.
  const distanceKm =
    385000.56 -
    20905 * Math.cos(Mp * dr) -
    3699 * Math.cos((2 * D - Mp) * dr) -
    2956 * Math.cos((2 * D) * dr) -
    570 * Math.cos((2 * Mp) * dr);

  // Obliquity of the ecliptic for the date (degrees).
  const epsilonDeg = 23.4393 - 0.0130042 * T;

  // Convert ecliptic (λ, β) to equatorial (RA, Dec).
  const lambda = lambdaDeg * dr;
  const beta = betaDeg * dr;
  const epsilon = epsilonDeg * dr;

  const sinDec =
    Math.sin(beta) * Math.cos(epsilon) +
    Math.cos(beta) * Math.sin(epsilon) * Math.sin(lambda);
  const decDeg = Math.asin(Math.max(-1, Math.min(1, sinDec))) * RAD;

  const raRad = Math.atan2(
    Math.sin(lambda) * Math.cos(epsilon) - Math.tan(beta) * Math.sin(epsilon),
    Math.cos(lambda),
  );
  const raDeg = (raRad * RAD + 360) % 360;
  const raHours = raDeg / 15;

  // Greenwich Mean Sidereal Time, low-precision (same formula as in
  // sun.ts — degrees per hour).
  const gmstHours = ((18.697374558 + 24.06570982441908 * n) % 24 + 24) % 24;
  const ghaDeg = normalize360(gmstHours * 15 - raDeg);

  // Horizontal parallax and semi-diameter from distance.
  const hpRad = Math.asin(EARTH_RADIUS_KM / distanceKm);
  const hpArcmin = (hpRad * RAD) * 60;
  // Geometric SD = arcsin(R_moon / Δ); for the teaching tool the
  // simple ratio HP · R_moon/R_earth matches the Almanac (no
  // augmentation correction, which is sub-arc-minute).
  const sdArcmin = hpArcmin * (MOON_RADIUS_KM / EARTH_RADIUS_KM);

  return {
    decDeg,
    ghaDeg,
    raHours,
    distanceKm,
    hpArcmin,
    sdArcmin,
  };
}

export type MoonPhaseName =
  | "New Moon"
  | "Waxing Crescent"
  | "First Quarter"
  | "Waxing Gibbous"
  | "Full Moon"
  | "Waning Gibbous"
  | "Last Quarter"
  | "Waning Crescent";

export interface MoonPhaseInfo {
  /// Fraction of the Moon's visible disc that is illuminated, 0..1.
  illumination: number;
  /// "Phase angle" along the synodic cycle, degrees [0, 360). 0 ≈ new
  /// moon, 90 ≈ first quarter, 180 ≈ full, 270 ≈ last quarter.
  phaseAngleDeg: number;
  /// Name of the phase the Moon is currently closest to.
  name: MoonPhaseName;
  /// True if waxing (Moon east of Sun in RA), false if waning.
  waxing: boolean;
  /// Approximate age in days since the most recent new moon, assuming
  /// a uniform synodic period of 29.53 days.
  ageDays: number;
}

const SYNODIC_DAYS = 29.530589;

/// Moon phase at the given date. Computed from the Moon–Sun
/// geocentric elongation: the angle subtended at Earth between Sun
/// and Moon. At new moon elongation ≈ 0; at full moon elongation ≈
/// 180°. The illuminated fraction is `(1 − cos φ) / 2` where φ is the
/// phase angle (which equals elongation for the waxing half of the
/// cycle, 360°−elongation for the waning half).
export function moonPhase(date: Date): MoonPhaseInfo {
  const sun = sunPosition(date);
  const moon = moonPosition(date);

  // Geocentric elongation between Sun and Moon via the standard
  // dot-product formula on the celestial sphere.
  const dRaDeg = normalize360((moon.raHours - sun.raHours) * 15);
  const cosE =
    Math.sin(sun.decDeg * DEG) * Math.sin(moon.decDeg * DEG) +
    Math.cos(sun.decDeg * DEG) *
      Math.cos(moon.decDeg * DEG) *
      Math.cos(dRaDeg * DEG);
  const elongationDeg =
    Math.acos(Math.max(-1, Math.min(1, cosE))) * RAD;

  // Waxing if Moon is east of Sun in RA (0 < dRaDeg < 180°).
  const waxing = dRaDeg < 180;
  const phaseAngleDeg = waxing ? elongationDeg : 360 - elongationDeg;
  const illumination = (1 - Math.cos(phaseAngleDeg * DEG)) / 2;
  const ageDays = (phaseAngleDeg / 360) * SYNODIC_DAYS;

  let name: MoonPhaseName;
  if (phaseAngleDeg < 22.5 || phaseAngleDeg >= 337.5) name = "New Moon";
  else if (phaseAngleDeg < 67.5) name = "Waxing Crescent";
  else if (phaseAngleDeg < 112.5) name = "First Quarter";
  else if (phaseAngleDeg < 157.5) name = "Waxing Gibbous";
  else if (phaseAngleDeg < 202.5) name = "Full Moon";
  else if (phaseAngleDeg < 247.5) name = "Waning Gibbous";
  else if (phaseAngleDeg < 292.5) name = "Last Quarter";
  else name = "Waning Crescent";

  return { illumination, phaseAngleDeg, name, waxing, ageDays };
}

export type LunarEvent = "new" | "first-quarter" | "full" | "last-quarter";

const PHASE_ANGLE_FOR: Record<LunarEvent, number> = {
  new: 0,
  "first-quarter": 90,
  full: 180,
  "last-quarter": 270,
};

/// Approximate date of the next phase event after `from`. The estimate
/// assumes uniform motion through the synodic cycle (accurate to
/// roughly ±12 hours; the Moon's true motion is non-uniform and
/// professional almanacs solve numerically).
export function nextLunarPhaseEvent(from: Date, event: LunarEvent): Date {
  const target = PHASE_ANGLE_FOR[event];
  const current = moonPhase(from).phaseAngleDeg;
  // Wrap forward into (0, 360]: if exactly at the event, advance a
  // full cycle so the caller always gets a future date.
  const ahead = (((target - current) % 360) + 360) % 360 || 360;
  return new Date(from.getTime() + (ahead / 360) * SYNODIC_DAYS * 86_400_000);
}
